/**
 * PresetCard Component
 *
 * Reusable preset card component used across the application.
 * Provides consistent styling and behavior for transformation presets.
 */

import React from 'react';
import { Badge } from '../ui/badge';
import { Sparkles, Clock, Home } from 'lucide-react';
import { TransformationPreset } from '../../services/job';
import {
  isAITransformation,
  getTransformationBadgeClass,
  getPresetCardClass,
  getPresetDescriptionClass,
  getPresetTitleClass
} from '../../utils/transformations';

interface PresetCardProps {
  presetKey: string;
  preset: TransformationPreset;
  onClick: () => void;
  maxTransformationsShown?: number;
}

export function PresetCard({
  presetKey,
  preset,
  onClick,
  maxTransformationsShown = 3
}: PresetCardProps) {
  const isGhostMannequin = presetKey === 'invisible_mannequin';
  const isGhostMannequinPro = presetKey === 'invisible_mannequin_pro';
  const isObjectPlacement = presetKey === 'object_placement';
  const isObjectPlacementPro = presetKey === 'object_placement_pro';

  return (
    <div
      className={`relative p-4 border-2 rounded-lg transition-all cursor-pointer ${getPresetCardClass(presetKey, preset.transformations)}`}
      onClick={onClick}
    >
      {/* Premium Badge for Ghost Mannequin */}
      {isGhostMannequin && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 animate-pulse">
          <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 text-xs px-3 py-1">
            ✨ Premium
          </Badge>
        </div>
      )}

      {/* Premium Pro Badge for Ghost Mannequin Pro */}
      {isGhostMannequinPro && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 animate-pulse">
          <Badge className="bg-gradient-to-r from-purple-700 via-pink-600 to-fuchsia-600 text-white border-0 text-xs px-3 py-1 shadow-lg">
            ⭐ Premium Pro
          </Badge>
        </div>
      )}

      {/* Virtual Staging Badge */}
      {isObjectPlacement && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 animate-pulse">
          <Badge className="bg-gradient-to-r from-amber-600 to-orange-600 text-white border-0 text-xs px-3 py-1">
            🏠 Virtual Staging
          </Badge>
        </div>
      )}

      {/* Virtual Staging Pro Badge */}
      {isObjectPlacementPro && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 animate-pulse">
          <Badge className="bg-gradient-to-r from-amber-700 via-orange-600 to-yellow-600 text-white border-0 text-xs px-3 py-1 shadow-lg">
            ⭐ Virtual Staging Pro
          </Badge>
        </div>
      )}

      {/* Header with Title and Badges */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <h4 className={`font-semibold ${getPresetTitleClass(presetKey)}`}>
            {preset.name}
          </h4>
          {isGhostMannequin && (
            <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
              (Fashion Industry Standard)
            </p>
          )}
          {isGhostMannequinPro && (
            <p className="text-xs text-purple-800 dark:text-purple-200 mt-0.5 font-semibold">
              (Enhanced AI Processing)
            </p>
          )}
          {isObjectPlacement && (
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              (Virtual Staging)
            </p>
          )}
          {isObjectPlacementPro && (
            <p className="text-xs text-amber-800 dark:text-amber-200 mt-0.5 font-semibold">
              (Enhanced AI Processing)
            </p>
          )}
        </div>

        <div className="flex gap-1">
          {/* Credits Badge */}
          {preset.estimatedCredits && (
            <Badge
              variant="outline"
              className={`text-xs ${
                isGhostMannequin
                  ? 'border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300'
                  : isObjectPlacement
                    ? 'border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-300'
                    : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              {preset.estimatedCredits} credits
            </Badge>
          )}

          {/* AI Type Badge */}
          {isGhostMannequin ? (
            <Badge className="text-xs text-white bg-gradient-to-r from-purple-500 to-pink-500">
              <Sparkles className="mr-1 w-3 h-3" />
              AI Pro
            </Badge>
          ) : isGhostMannequinPro ? (
            <Badge className="text-xs text-white bg-gradient-to-r from-purple-600 via-pink-600 to-fuchsia-600 shadow-md">
              <Sparkles className="mr-1 w-3 h-3" />
              AI Pro Ultra
            </Badge>
          ) : isObjectPlacement ? (
            <Badge className="text-xs text-white bg-gradient-to-r from-amber-500 to-orange-500">
              <Home className="mr-1 w-3 h-3" />
              Virtual Staging
            </Badge>
          ) : isObjectPlacementPro ? (
            <Badge className="text-xs text-white bg-gradient-to-r from-amber-600 via-orange-600 to-yellow-600 shadow-md">
              <Sparkles className="mr-1 w-3 h-3" />
              AI Pro Ultra
            </Badge>
          ) : preset.transformations.some(t => isAITransformation(t.type)) && (
            <Badge className="text-xs text-white bg-gradient-to-r from-blue-500 to-purple-500">
              <Sparkles className="mr-1 w-3 h-3" />
              AI
            </Badge>
          )}
        </div>
      </div>

      {/* Description */}
      <p className={`mb-2 text-sm ${getPresetDescriptionClass(presetKey)}`}>
        {preset.description}
      </p>

      {/* Transformation Chips */}
      <div className="flex flex-wrap gap-1 mb-2">
        {preset.transformations.slice(0, maxTransformationsShown).map((t, i) => (
          <Badge
            key={i}
            variant="secondary"
            className={`text-xs flex items-center gap-1 ${getTransformationBadgeClass(t.type)}`}
          >
            {t.type.replace(/_/g, ' ')}
            {isAITransformation(t.type) &&
             t.type !== 'invisible_mannequin' &&
             t.type !== 'object_placement' &&
             t.type !== 'invisible_mannequin_pro' &&
             t.type !== 'object_placement_pro' && (
              <Sparkles className="w-3 h-3" />
            )}
          </Badge>
        ))}
        {preset.transformations.length > maxTransformationsShown && (
          <Badge
            variant="outline"
            className={`text-xs ${
              isGhostMannequin || isGhostMannequinPro
                ? 'text-purple-600 dark:text-purple-400'
                : isObjectPlacement || isObjectPlacementPro
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground dark:text-gray-400'
            }`}
          >
            +{preset.transformations.length - maxTransformationsShown} more
          </Badge>
        )}
      </div>

      {/* Processing Time */}
      {preset.processingTime && (
        <div className={`text-xs flex items-center gap-1 ${
          isGhostMannequin
            ? 'text-purple-600 dark:text-purple-400'
            : isObjectPlacement
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-muted-foreground dark:text-gray-400'
        }`}>
          <Clock className="w-3 h-3" />
          {preset.processingTime}
        </div>
      )}
    </div>
  );
}
